import { useRef, useState } from 'react'
import styled from 'styled-components'
import { Manager, Reference, Popper } from 'react-popper'

import useDocumentClick from 'hooks/useDocumentClick'
import ChevronDownIcon from '../components/icons/ChevronDown'
import AccommodationIcon from '../components/icons/Cabin'
import AdventureIcon from '../components/icons/Experience'
import InvisibleButton from '../components/display/InvisibleButton'
import { mediaMax } from '../styles/media'
import typeStyle from '../styles/typeStyle'

import { SearchType } from './SearchTypeToggle'

const Wrapper = styled.div`
  margin: 8px;
`

const ButtonWrapper = styled.div<{ isActive: boolean }>`
  ${typeStyle('-ds-font-caption-regular')}
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0.666em 0.8333em;

  background-color: white;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  cursor: pointer;

  ${({ isActive, theme }) =>
    isActive &&
    `
    color: #fff;
    background-color: ${theme.brand.jungle};
    border-color: ${theme.brand.jungle};
  `}
`

const ButtonInner = styled(InvisibleButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;

  svg {
    width: 12px;
    height: 12px;
  }
`

const Label = styled.span`
  margin-right: 8px;
  font-size: 16px;

  ${mediaMax.xl`
    margin-right: 0;
    font-size: 12px;
  `}
`

const ChevronWrapper = styled.div`
  display: flex;
  align-items: center;

  ${mediaMax.xl`
    display: none;
  `}
`

const Panel = styled.div`
  position: relative;
  top: 8px;
  min-width: 200px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgba(31, 23, 23, 0.15);
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  z-index: 10;
  overflow: hidden;
`

const OptionButton = styled.button`
  ${typeStyle('-ds-font-body-sm-regular')}
  appearance: none;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  background-color: white;
  border: none;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    margin-right: 12px;
    flex-shrink: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme?.['-ds-neutral-200']?.value};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  }
`

const Arrow = styled.div`
  position: absolute;
  top: 0;
  height: 12px;
  width: 12px;

  &:after,
  &:before {
    bottom: 100%;
    left: 50%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #ffffff;
    border-width: 8px;
    margin-left: -8px;
  }
  &:before {
    border-color: rgba(213, 213, 213, 0);
    border-bottom-color: #d5d5d5;
    border-width: 9px;
    margin-left: -9px;
  }
`

interface SearchTypeDropdownProps {
  currentSearchType: Maybe<GQL_ListingTypes>
  setPrimaryFilterType: (type: SearchType) => any
}

const modifiers = [
  {
    name: 'preventOverflow',
    options: {
      padding: 22,
      enabled: true,
    },
  },
]

const SearchTypeDropdown = ({
  currentSearchType,
  setPrimaryFilterType,
}: SearchTypeDropdownProps) => {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleDocumentClick = (e: Event) => {
    const clickWasInsidePanel = panelRef.current?.contains(e.target as Node)
    const clickWasInsideTrigger = triggerRef.current?.contains(e.target as Node)

    if (!clickWasInsidePanel && !clickWasInsideTrigger) {
      setOpen(false)
    }
  }

  useDocumentClick(handleDocumentClick)

  const handleSelectAccommodation = () => {
    setPrimaryFilterType('accommodation')
    setOpen(false)
  }

  const handleSelectAdventures = () => {
    setPrimaryFilterType('adventures')
    setOpen(false)
  }

  const label =
    currentSearchType === 'adventures' ? 'Adventures' : 'Stories & Stays'

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <Wrapper ref={ref}>
            <ButtonWrapper isActive={open}>
              <ButtonInner
                ref={triggerRef}
                onClick={handleToggle}
                aria-label="Select search type"
              >
                <Label>{label}</Label>
                <ChevronWrapper>
                  <ChevronDownIcon />
                </ChevronWrapper>
              </ButtonInner>
            </ButtonWrapper>
          </Wrapper>
        )}
      </Reference>
      <Popper placement="bottom-start" modifiers={modifiers}>
        {({ ref, style, placement, arrowProps }) =>
          open && (
            <div ref={ref} style={style} data-placement={placement}>
              <Panel ref={panelRef}>
                <OptionButton
                  onClick={handleSelectAccommodation}
                  aria-label="View Stories & Stays"
                >
                  <AccommodationIcon />
                  Stories & Stays
                </OptionButton>
                <OptionButton
                  onClick={handleSelectAdventures}
                  aria-label="View Adventures"
                >
                  <AdventureIcon />
                  Adventures
                </OptionButton>
                <Arrow ref={arrowProps.ref} style={arrowProps.style} />
              </Panel>
            </div>
          )
        }
      </Popper>
    </Manager>
  )
}

export default SearchTypeDropdown
